<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import i18n from "@/i18n";
import { salesTargetMethods } from "@/state/helpers";
import SalesTargetFormPanel from "./SalesTargetFormPanel.vue";

const PERIOD_LABEL = { monthly: "Monthly", quarterly: "Quarterly", yearly: "Yearly" };

export default {
  page() {
    return { title: `Sales Target — ${this.agentCode}` };
  },
  components: { Layout, PageHeader, SalesTargetFormPanel },
  data() {
    return {
      loading: true,
      agentName: null,
      plans: [],
      expandedPlanId: null,
    };
  },
  computed: {
    agentCode() { return this.$route.params.agentCode; },
    title() { return `${this.agentCode}${this.agentName ? ' — ' + this.agentName : ''}`; },
    items() {
      return [
        { text: i18n.t("module.management"), href: "/" },
        { text: i18n.t("module.salesTarget"), href: "/sales-targets" },
        { text: this.agentCode, active: true },
      ];
    },
  },
  async created() { await this.fetch(); },
  methods: {
    ...salesTargetMethods,
    async fetch() {
      this.loading = true;
      const r = await this.getAgentDetail({ agentCode: this.agentCode });
      const d = r?.data || {};
      this.agentName = d.agent_name || null;
      this.plans = d.plans || [];
      this.loading = false;
    },
    periodLabel(code) { return PERIOD_LABEL[code] || code; },
    fmtRm(v) {
      const n = Number(v) || 0;
      return n.toLocaleString(undefined, { style: "currency", currency: "MYR", minimumFractionDigits: 2 });
    },
    toggleExpand(plan) {
      this.expandedPlanId = this.expandedPlanId === plan.id ? null : plan.id;
    },
    async remove(plan) {
      if (!confirm(`Delete plan "${plan.name}"?`)) return;
      await this.deleteSalesTarget({ id: plan.id, filter: null });
      await this.fetch();
    },
    async onSaved() {
      this.expandedPlanId = null;
      await this.fetch();
    },
    progressClass(pct) {
      if (pct >= 100) return "achieved";
      if (pct >= 50) return "on-track";
      return "behind";
    },
    goBack() { this.$router.push({ name: "Sales-target-listing" }); },
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />

    <div v-if="loading" class="text-center py-5">
      <b-spinner small variant="primary"></b-spinner>
    </div>

    <div v-else>
      <!-- SECTION 1: Records & Progress -->
      <div class="row">
        <div class="col-12">
          <div class="card">
            <div class="card-body">
              <div class="section-header">
                <h5 class="section-header-title">
                  <i class="mdi mdi-target"></i> {{ $t('salesTarget.agentDetail.recordsProgress') }}
                </h5>
                <span class="text-muted small">{{ $t('salesTarget.agentDetail.planCount', [plans.length]) }}</span>
              </div>

              <div v-if="!plans.length" class="empty-state">
                <i class="mdi mdi-target display-4 text-muted"></i>
                <p class="text-muted mb-0">{{ $t('salesTarget.agentDetail.noPlans') }}</p>
              </div>

              <div v-else>
                <div v-for="plan in plans" :key="plan.id" class="plan-card">
                  <div class="plan-head" @click="toggleExpand(plan)">
                    <div class="plan-name">
                      <i :class="['mdi', expandedPlanId === plan.id ? 'mdi-chevron-down' : 'mdi-chevron-right']"></i>
                      <strong>{{ plan.name }}</strong>
                      <b-badge variant="info" class="ml-2">{{ periodLabel(plan.period_type) }}</b-badge>
                      <b-badge v-for="ac in (plan.area_codes || [])" :key="ac" variant="light" class="ml-1">
                        {{ ac }}
                      </b-badge>
                    </div>
                    <div class="plan-stats">
                      <div class="stat-block">
                        <div class="stat-label">{{ $t('salesTarget.agentDetail.periods') }}</div>
                        <div class="stat-value">{{ plan.periods_count }}</div>
                      </div>
                      <div class="stat-block">
                        <div class="stat-label">{{ $t('salesTarget.agentDetail.totalTarget') }}</div>
                        <div class="stat-value">{{ fmtRm(plan.total_target) }}</div>
                      </div>
                      <div class="stat-block">
                        <div class="stat-label">{{ $t('salesTarget.agentDetail.actual') }}</div>
                        <div class="stat-value">{{ fmtRm(plan.total_actual) }}</div>
                      </div>
                      <div class="stat-block">
                        <div class="stat-label">{{ $t('salesTarget.agentDetail.progress') }}</div>
                        <div :class="['stat-value', 'progress-text', progressClass(plan.total_percentage)]">
                          {{ plan.total_percentage }}%
                        </div>
                      </div>
                      <div class="action-buttons" @click.stop>
                        <a v-if="$can('Update Sales Target')" href="javascript:void(0);" @click="expandedPlanId = plan.id" class="text-info" v-b-tooltip.hover title="Edit">
                          <i class="mdi mdi-pencil font-size-18"></i>
                        </a>
                        <a v-if="$can('Delete Sales Target')" href="javascript:void(0);" @click="remove(plan)" class="text-danger" v-b-tooltip.hover title="Delete">
                          <i class="mdi mdi-delete font-size-18"></i>
                        </a>
                      </div>
                    </div>
                  </div>

                  <b-collapse :visible="expandedPlanId === plan.id">
                    <div class="plan-body">
                      <div class="breakdown-section">
                        <h6 class="section-title">
                          <i class="mdi mdi-format-list-numbered"></i> {{ $t('salesTarget.agentDetail.periodBreakdown') }}
                        </h6>
                        <div class="breakdown-grid">
                          <div
                            v-for="d in plan.details"
                            :key="d.id"
                            :class="['breakdown-item', { current: d.is_current }]"
                          >
                            <div class="bi-label">{{ d.label }}</div>
                            <div class="bi-range">{{ d.period_start }} → {{ d.period_end }}</div>
                            <div class="bi-progress-bar">
                              <div
                                class="bi-progress-fill"
                                :style="{ width: Math.min(d.percentage || 0, 100) + '%' }"
                                :class="progressClass(d.percentage || 0)"
                              ></div>
                            </div>
                            <div class="bi-amounts">
                              <span>{{ fmtRm(d.actual_amount) }}</span>
                              <span class="bi-target">/ {{ fmtRm(d.target_amount) }}</span>
                            </div>
                            <div class="bi-pct" :class="progressClass(d.percentage || 0)">{{ d.percentage }}%</div>
                          </div>
                        </div>
                      </div>

                      <div v-if="$can('Update Sales Target')" class="edit-section">
                        <h6 class="section-title">
                          <i class="mdi mdi-pencil"></i> {{ $t('salesTarget.agentDetail.editPlan') }}
                        </h6>
                        <SalesTargetFormPanel
                          mode="edit"
                          :agent-code="agentCode"
                          :target="plan"
                          @saved="onSaved"
                          @cancel="expandedPlanId = null"
                        />
                      </div>
                    </div>
                  </b-collapse>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- SECTION 2: Create New -->
      <div class="row" v-if="$can('Create Sales Target')">
        <div class="col-12">
          <div class="card">
            <div class="card-body">
              <div class="section-header">
                <h5 class="section-header-title">
                  <i class="mdi mdi-plus-circle"></i> {{ $t('salesTarget.agentDetail.createNew') }}
                </h5>
              </div>
              <SalesTargetFormPanel
                mode="create"
                :agent-code="agentCode"
                :existing-plans="plans"
                @saved="onSaved"
                @cancel="goBack"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<style scoped>
.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e9ecef;
}
.section-header-title {
  font-size: 14px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #7C4DFF;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 6px;
}
.section-header-title i { font-size: 18px; }

.empty-state {
  text-align: center;
  padding: 60px 20px;
}
.plan-card {
  border: 1px solid #e9ecef;
  border-radius: 10px;
  margin-bottom: 12px;
  overflow: hidden;
  background: #fff;
}
.plan-head {
  padding: 14px 18px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  gap: 16px;
}
.plan-head:hover { background: rgba(124, 77, 255, 0.03); }
.plan-name {
  flex: 1; min-width: 0;
  display: flex; align-items: center; flex-wrap: wrap; gap: 4px;
  font-size: 14px;
}
.plan-name i { color: #8993A4; font-size: 18px; }
.plan-stats { display: flex; align-items: center; gap: 24px; }
.stat-block { text-align: center; }
.stat-label {
  font-size: 10px; text-transform: uppercase; letter-spacing: 0.3px;
  color: #8993A4; font-weight: 600;
}
.stat-value { font-size: 14px; font-weight: 700; color: #212529; margin-top: 2px; }
.progress-text.achieved { color: #10B981; }
.progress-text.on-track { color: #7C4DFF; }
.progress-text.behind { color: #F59E0B; }
.action-buttons { display: flex; gap: 10px; padding-left: 8px; border-left: 1px solid #e9ecef; }

.plan-body {
  padding: 16px 18px;
  background: #fafbfc;
  border-top: 1px solid #e9ecef;
}
.section-title {
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #7C4DFF;
  margin-bottom: 12px;
  display: flex; align-items: center; gap: 4px;
}
.section-title i { font-size: 16px; }
.breakdown-section { margin-bottom: 24px; }
.breakdown-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 10px;
}
.breakdown-item {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 10px 12px;
  position: relative;
}
.breakdown-item.current {
  border-color: #7C4DFF;
  box-shadow: 0 0 0 2px rgba(124, 77, 255, 0.12);
}
.bi-label { font-size: 12px; font-weight: 700; color: #212529; }
.bi-range { font-size: 10px; color: #8993A4; margin-top: 2px; }
.bi-progress-bar {
  height: 5px;
  border-radius: 3px;
  background: #f1f3f5;
  margin: 8px 0 6px;
  overflow: hidden;
}
.bi-progress-fill { height: 100%; transition: width 0.3s; }
.bi-progress-fill.achieved { background: #10B981; }
.bi-progress-fill.on-track { background: #7C4DFF; }
.bi-progress-fill.behind { background: #F59E0B; }
.bi-amounts { font-size: 11px; font-weight: 700; color: #212529; }
.bi-target { font-weight: 400; color: #8993A4; margin-left: 2px; }
.bi-pct {
  position: absolute; top: 10px; right: 12px;
  font-size: 11px; font-weight: 700;
}
.bi-pct.achieved { color: #10B981; }
.bi-pct.on-track { color: #7C4DFF; }
.bi-pct.behind { color: #F59E0B; }

.edit-section {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 16px;
}
</style>
